import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { submitWorkspaceActivationRequest } from '@services/workspaceActivation';
import copyToClipboard from '@utils/copyToClipboard';

const MEETING_URL = import.meta.env.VITE_WORKSPACE_ACTIVATION_MEETING_URL;
const SALES_EMAIL = import.meta.env.VITE_SALES_CONTACT_EMAIL || 'sales@qualz.ai';

const INTERVIEW_VOLUME_OPTIONS = ['0-20', '20-100', '100-500', '500+'];
const SURVEY_RESPONSE_VOLUME_OPTIONS = ['0-500', '500-5000', '5000+'];

const PRIMARY_USE_CASES = [
  {
    label: 'AI moderated interviews',
    volumeKey: 'ai_moderated_interviews_volume',
    volumeLabel: 'Estimated interviews',
    volumeOptions: INTERVIEW_VOLUME_OPTIONS,
  },
  {
    label: 'AI participant (synthetic users) responses',
    volumeKey: 'ai_participant_responses_volume',
    volumeLabel: 'Estimated responses',
    volumeOptions: INTERVIEW_VOLUME_OPTIONS,
  },
  {
    label: 'Survey response analysis',
    volumeKey: 'survey_response_analysis_volume',
    volumeLabel: 'Estimated responses',
    volumeOptions: SURVEY_RESPONSE_VOLUME_OPTIONS,
    infoText:
      'With an activated workspace, there is no charge for data collection and general analysis. Only AI analysis of open-ended responses cost entitlements.',
  },
  {
    label: 'Qualitative analysis',
    volumeKey: 'qualitative_analysis_volume',
    volumeLabel: 'Estimated studies',
    volumeOptions: INTERVIEW_VOLUME_OPTIONS,
    followupKeys: ['custom_analysis_needed', 'slides_report_needed'],
  },
  {
    label: 'Research operations & compliance',
    followupKeys: ['gdpr_compliant', 'data_residency_required'],
  },
];

const SERVICE_OPTIONS = [
  {
    label: 'End-to-end research support',
    tooltip: "We can help 'run' the project end-to-end for you if desired.",
  },
];

const WorkspaceActivationContent = ({ prefill = {}, isExpired = false, variant = 'full' }) => {
  const isEmbedded = variant === 'embedded';
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const [emailCopied, setEmailCopied] = useState(false);
  const copyTimeoutRef = useRef(null);
  const [formData, setFormData] = useState({
    user_name: prefill.user_name || '',
    user_email: prefill.user_email || '',
    company_name: prefill.company_name || '',
    role_title: prefill.role_title || '',
    workspace_id: prefill.workspace_id || '',
    workspace_name: prefill.workspace_name || '',
    workspace_member_count: '',
    primary_use_cases: [],
    ai_moderated_interviews_volume: '',
    ai_participant_responses_volume: '',
    survey_response_analysis_volume: '',
    qualitative_analysis_volume: '',
    custom_analysis_needed: '',
    slides_report_needed: '',
    gdpr_compliant: '',
    data_residency_required: '',
    data_residency_details: '',
    recruitment_support: 'not_sure',
    additional_services: [],
    project_description: '',
    notes: '',
    source: prefill.source || 'app',
  });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      user_name: prefill.user_name || prev.user_name,
      user_email: prefill.user_email || prev.user_email,
      company_name: prefill.company_name || prev.company_name,
      role_title: prefill.role_title || prev.role_title,
      workspace_id: prefill.workspace_id || prev.workspace_id,
      workspace_name: prefill.workspace_name || prev.workspace_name,
      source: prefill.source || prev.source,
    }));
  }, [prefill]);

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    },
    [],
  );

  const handleToggle = (field, value) => {
    setFormData((prev) => {
      const currentValues = prev[field];
      const exists = currentValues.includes(value);
      return {
        ...prev,
        [field]: exists
          ? currentValues.filter((item) => item !== value)
          : [...currentValues, value],
      };
    });
  };

  const handlePrimaryUseCaseToggle = (item) => {
    setFormData((prev) => {
      const currentValues = prev.primary_use_cases;
      const exists = currentValues.includes(item.label);
      const nextValues = exists
        ? currentValues.filter((useCase) => useCase !== item.label)
        : [...currentValues, item.label];
      const nextState = {
        ...prev,
        primary_use_cases: nextValues,
      };
      if (exists && item.volumeKey) {
        nextState[item.volumeKey] = '';
      }
      if (exists && item.followupKeys) {
        item.followupKeys.forEach((key) => {
          nextState[key] = '';
        });
      }
      if (!exists && item.followupKeys) {
        item.followupKeys.forEach((key) => {
          if (!nextState[key]) {
            nextState[key] = 'not_sure';
          }
        });
      }
      if (exists && item.label === 'Research operations & compliance') {
        nextState.data_residency_details = '';
      }
      return nextState;
    });
  };

  const validateForm = () => {
    if (!formData.user_name.trim()) {
      return 'Please add your name.';
    }
    if (!formData.user_email.trim()) {
      return 'Please add your email.';
    }
    if (parseNumber(formData.workspace_member_count) === null) {
      return 'Please add the number of workspace members.';
    }
    return '';
  };

  const parseNumber = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    return parsed < 0 ? null : parsed;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      setStatus({ state: 'error', message: error });
      return;
    }
    setStatus({ state: 'loading', message: '' });
    try {
      const qualitativeSelected = formData.primary_use_cases.includes('Qualitative analysis');
      const researchOpsSelected = formData.primary_use_cases.includes(
        'Research operations & compliance',
      );
      const payload = {
        ...formData,
        workspace_member_count: parseNumber(formData.workspace_member_count),
        project_description: formData.project_description.trim() || null,
        ai_moderated_interviews_volume: formData.ai_moderated_interviews_volume || null,
        ai_participant_responses_volume: formData.ai_participant_responses_volume || null,
        survey_response_analysis_volume: formData.survey_response_analysis_volume || null,
        qualitative_analysis_volume: formData.qualitative_analysis_volume || null,
        custom_analysis_needed: qualitativeSelected ? formData.custom_analysis_needed : null,
        slides_report_needed: qualitativeSelected ? formData.slides_report_needed : null,
        gdpr_compliant: researchOpsSelected ? formData.gdpr_compliant : null,
        data_residency_required: researchOpsSelected ? formData.data_residency_required : null,
        data_residency_details:
          researchOpsSelected && formData.data_residency_required === 'yes'
            ? formData.data_residency_details.trim() || null
            : null,
      };
      await submitWorkspaceActivationRequest(payload);
      setStatus({
        state: 'success',
        message: 'Thanks! We received your request and will be in touch shortly.',
      });
    } catch (err) {
      setStatus({
        state: 'error',
        message: err?.detail || 'Something went wrong while submitting. Please try again.',
      });
    }
  };

  const handleCopyEmail = async () => {
    await copyToClipboard(SALES_EMAIL, () => {
      setEmailCopied(true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => setEmailCopied(false), 2000);
    });
  };

  return (
    <Box
      sx={{
        background: isEmbedded
          ? 'transparent'
          : 'linear-gradient(135deg, #FFFFFF 0%, #F5F3FF 100%)',
        py: isEmbedded ? { xs: 4, md: 6 } : { xs: 6, md: 10 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6}>
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              <Typography variant="h3" fontWeight={700} color="#111827">
                Activate your Qualz workspace
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Deliver research outcomes in days, not months. Qualz pairs AI-led interviewing with
                expert-quality analysis and hands-on support so your team can move faster with
                confidence.
              </Typography>
              <Stack spacing={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                  What you unlock
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2">
                    • 4 months of research compressed into 4 days
                  </Typography>
                  <Typography variant="body2">
                    • Industry-leading interview and analysis quality
                  </Typography>
                  <Typography variant="body2">
                    • Dedicated research support for complex studies
                  </Typography>
                  <Typography variant="body2">
                    • Full-service options: recruitment, reporting, and synthesis
                  </Typography>
                </Stack>
              </Stack>

              {isExpired && (
                <Alert severity="warning">
                  This workspace is expired. Contact sales to renew or request a new activation.
                </Alert>
              )}

              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  background: '#fff',
                  borderRadius: 2,
                  boxShadow: '0 20px 40px rgba(22, 44, 69, 0.08)',
                }}
              >
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Get Started Right Away
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Book a 20-minute walkthrough and we’ll tailor the workspace to your research
                    goals.
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      component="a"
                      href={MEETING_URL || '#'}
                      target="_blank"
                      rel="noreferrer"
                      disabled={!MEETING_URL}
                    >
                      Schedule a Call
                    </Button>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Email:
                      </Typography>
                      <Link
                        href="#"
                        underline="always"
                        color="primary"
                        onClick={(event) => event.preventDefault()}
                        sx={{ fontWeight: 600 }}
                      >
                        {SALES_EMAIL}
                      </Link>
                      <Tooltip title={emailCopied ? 'Copied' : 'Copy email'}>
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="Copy sales email"
                          onClick={handleCopyEmail}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    If you already have an account representative, contact them directly.
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                background: '#ffffff',
                boxShadow: '0 24px 60px rgba(31, 64, 104, 0.12)',
              }}
            >
              <Stack spacing={3}>
                <Typography variant="h5" fontWeight={700}>
                  Tell us about your research
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Share a few details so we can recommend the right setup and activate your
                  workspace quickly.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This information helps us generate a quote for services. Entitlements can be
                  upgraded later by contacting your account representative.
                </Typography>

                {status.state === 'success' && <Alert severity="success">{status.message}</Alert>}
                {status.state === 'error' && <Alert severity="error">{status.message}</Alert>}

                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Full name"
                          fullWidth
                          value={formData.user_name}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, user_name: e.target.value }))
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Work email"
                          type="email"
                          fullWidth
                          value={formData.user_email}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              user_email: e.target.value,
                            }))
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Company"
                          fullWidth
                          value={formData.company_name}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              company_name: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Role / title"
                          fullWidth
                          value={formData.role_title}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, role_title: e.target.value }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Workspace name (if known)"
                          fullWidth
                          value={formData.workspace_name}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              workspace_name: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Workspace ID (if known)"
                          fullWidth
                          value={formData.workspace_id}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              workspace_id: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="How many members in the workspace?"
                          type="number"
                          fullWidth
                          value={formData.workspace_member_count}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              workspace_member_count: e.target.value,
                            }))
                          }
                          inputProps={{ min: 0 }}
                          required
                        />
                      </Grid>
                    </Grid>

                    <Divider />

                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>Estimated Yearly Volume</span>
                          <Tooltip title="If you are estimating for a single project, just use those numbers.">
                            <InfoOutlinedIcon fontSize="small" color="action" />
                          </Tooltip>
                        </Stack>
                      </FormLabel>
                      <FormGroup>
                        {PRIMARY_USE_CASES.map((item) => {
                          const isSelected = formData.primary_use_cases.includes(item.label);
                          const labelContent = (
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <span>{item.label}</span>
                              {item.infoText && (
                                <Tooltip title={item.infoText}>
                                  <InfoOutlinedIcon fontSize="small" color="action" />
                                </Tooltip>
                              )}
                            </Stack>
                          );
                          return (
                            <Box key={item.label} sx={{ mb: item.volumeKey ? 2 : 0 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handlePrimaryUseCaseToggle(item)}
                                  />
                                }
                                label={labelContent}
                              />
                              {isSelected && (
                                <Box sx={{ pl: 4 }}>
                                  {item.volumeKey &&
                                    (item.volumeOptions ? (
                                      <TextField
                                        label={item.volumeLabel}
                                        select
                                        fullWidth
                                        value={formData[item.volumeKey]}
                                        onChange={(e) =>
                                          setFormData((prev) => ({
                                            ...prev,
                                            [item.volumeKey]: e.target.value,
                                          }))
                                        }
                                        helperText="Select a range"
                                        sx={{ maxWidth: 320 }}
                                      >
                                        {item.volumeOptions.map((option) => (
                                          <MenuItem key={option} value={option}>
                                            {option}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    ) : (
                                      <TextField
                                        label={item.volumeLabel || 'Estimated volume'}
                                        type="number"
                                        fullWidth
                                        value={formData[item.volumeKey]}
                                        onChange={(e) =>
                                          setFormData((prev) => ({
                                            ...prev,
                                            [item.volumeKey]: e.target.value,
                                          }))
                                        }
                                        placeholder={item.volumeExample}
                                        helperText={
                                          item.volumeExample && item.volumeUnit
                                            ? `e.g., ${item.volumeExample} ${item.volumeUnit}`
                                            : undefined
                                        }
                                        inputProps={{ min: 0 }}
                                        sx={{ maxWidth: 320 }}
                                      />
                                    ))}
                                  {item.followupKeys && (
                                    <Stack spacing={2} sx={{ mt: item.volumeKey ? 2 : 1 }}>
                                      {item.label === 'Qualitative analysis' && (
                                        <>
                                          <FormControl component="fieldset">
                                            <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                                              <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                              >
                                                <span>Do you need custom analysis?</span>
                                                <Tooltip title="Custom analysis is when you want to use your own framework, beyond the 14 frameworks or lenses already supported.">
                                                  <InfoOutlinedIcon
                                                    fontSize="small"
                                                    color="action"
                                                  />
                                                </Tooltip>
                                              </Stack>
                                            </FormLabel>
                                            <RadioGroup
                                              row
                                              value={formData.custom_analysis_needed}
                                              onChange={(e) =>
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  custom_analysis_needed: e.target.value,
                                                }))
                                              }
                                            >
                                              <FormControlLabel
                                                value="yes"
                                                control={<Radio />}
                                                label="Yes"
                                              />
                                              <FormControlLabel
                                                value="no"
                                                control={<Radio />}
                                                label="No"
                                              />
                                              <FormControlLabel
                                                value="not_sure"
                                                control={<Radio />}
                                                label="Not sure"
                                              />
                                            </RadioGroup>
                                          </FormControl>
                                          <FormControl component="fieldset">
                                            <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                                              Do you need slides and report built?
                                            </FormLabel>
                                            <RadioGroup
                                              row
                                              value={formData.slides_report_needed}
                                              onChange={(e) =>
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  slides_report_needed: e.target.value,
                                                }))
                                              }
                                            >
                                              <FormControlLabel
                                                value="yes"
                                                control={<Radio />}
                                                label="Yes"
                                              />
                                              <FormControlLabel
                                                value="no"
                                                control={<Radio />}
                                                label="No"
                                              />
                                              <FormControlLabel
                                                value="not_sure"
                                                control={<Radio />}
                                                label="Not sure"
                                              />
                                            </RadioGroup>
                                          </FormControl>
                                        </>
                                      )}
                                      {item.label === 'Research operations & compliance' && (
                                        <>
                                          <FormControl component="fieldset">
                                            <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                                              GDPR compliant?
                                            </FormLabel>
                                            <RadioGroup
                                              row
                                              value={formData.gdpr_compliant}
                                              onChange={(e) =>
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  gdpr_compliant: e.target.value,
                                                }))
                                              }
                                            >
                                              <FormControlLabel
                                                value="yes"
                                                control={<Radio />}
                                                label="Yes"
                                              />
                                              <FormControlLabel
                                                value="no"
                                                control={<Radio />}
                                                label="No"
                                              />
                                              <FormControlLabel
                                                value="not_sure"
                                                control={<Radio />}
                                                label="Not sure"
                                              />
                                            </RadioGroup>
                                          </FormControl>
                                          <FormControl component="fieldset">
                                            <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                                              Other data residency requirements?
                                            </FormLabel>
                                            <RadioGroup
                                              row
                                              value={formData.data_residency_required}
                                              onChange={(e) =>
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  data_residency_required: e.target.value,
                                                }))
                                              }
                                            >
                                              <FormControlLabel
                                                value="yes"
                                                control={<Radio />}
                                                label="Yes"
                                              />
                                              <FormControlLabel
                                                value="no"
                                                control={<Radio />}
                                                label="No"
                                              />
                                              <FormControlLabel
                                                value="not_sure"
                                                control={<Radio />}
                                                label="Not sure"
                                              />
                                            </RadioGroup>
                                          </FormControl>
                                          {formData.data_residency_required === 'yes' && (
                                            <TextField
                                              label="Please specify data residency requirements"
                                              fullWidth
                                              value={formData.data_residency_details}
                                              onChange={(e) =>
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  data_residency_details: e.target.value,
                                                }))
                                              }
                                            />
                                          )}
                                        </>
                                      )}
                                    </Stack>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </FormGroup>
                    </FormControl>

                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>Do you need recruitment support?</span>
                          <Tooltip title="We have access to 13M+ B2B and 100M B2C participants through our partner recruitment providers. We can also work with your internal and external providers.">
                            <InfoOutlinedIcon fontSize="small" color="action" />
                          </Tooltip>
                        </Stack>
                      </FormLabel>
                      <RadioGroup
                        row
                        value={formData.recruitment_support}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            recruitment_support: e.target.value,
                          }))
                        }
                      >
                        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="no" control={<Radio />} label="No" />
                        <FormControlLabel value="not_sure" control={<Radio />} label="Not sure" />
                      </RadioGroup>
                    </FormControl>

                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                        Services
                      </FormLabel>
                      <FormGroup>
                        {SERVICE_OPTIONS.map((item) => (
                          <FormControlLabel
                            key={item.label}
                            control={
                              <Checkbox
                                checked={formData.additional_services.includes(item.label)}
                                onChange={() => handleToggle('additional_services', item.label)}
                              />
                            }
                            label={
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <span>{item.label}</span>
                                {item.tooltip && (
                                  <Tooltip title={item.tooltip}>
                                    <InfoOutlinedIcon fontSize="small" color="action" />
                                  </Tooltip>
                                )}
                              </Stack>
                            }
                          />
                        ))}
                      </FormGroup>
                    </FormControl>
                    <TextField
                      label="Anything else we should know?"
                      multiline
                      minRows={3}
                      fullWidth
                      value={formData.notes}
                      onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                    />

                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems={{ sm: 'center' }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={status.state === 'loading' || status.state === 'success'}
                      >
                        {status.state === 'loading'
                          ? 'Sending...'
                          : status.state === 'success'
                            ? 'Request submitted'
                            : 'Submit request'}
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        You will receive a confirmation email with next steps and a demo link.
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                <Divider />

                <Typography variant="caption" color="text.secondary">
                  Questions? Email us at{' '}
                  <Link href={`mailto:${SALES_EMAIL}`} underline="hover">
                    {SALES_EMAIL}
                  </Link>
                  .
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default WorkspaceActivationContent;
